import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './StoryTheater.css';

interface StoryLike {
  id: string;
  mediaUrl?: string;
  mediaType?: string;
  timestamp: Date;
  content?: string;
  expiresAt?: Date;
}

interface StoryTheaterProps {
  story: StoryLike;
  stories: StoryLike[];
  index: number;
  adminUser: { avatar: string; displayName: string };
  currentUser: { avatar?: string; displayName?: string };
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onDelete: () => void;
  formatTime: (d: Date) => string;
}

/**
 * Story Theater — protected media review surface.
 *
 * Architectural contract:
 *   - Portaled to <body> so it is not coupled to dashboard layout.
 *   - Backdrop is viewport-relative (position: fixed, inset: 0).
 *   - Media stage uses `object-fit: contain` with a blurred matte so the
 *     full asset is preserved (admin review context — never crop).
 *   - Body scroll is locked while open.
 *   - Escape closes; ArrowLeft/ArrowRight navigate.
 *   - role="dialog" + aria-modal="true".
 *   - z-index from a token (--z-modal-backdrop / --z-modal-dialog).
 *
 * EXIF orientation is corrected at render via CSS `image-orientation:
 * from-image` as a safety net. Long-term, normalization should happen in
 * the upload pipeline; this CSS is the fallback.
 */
const StoryTheater: React.FC<StoryTheaterProps> = ({
  story, stories, index, adminUser, currentUser,
  onClose, onPrev, onNext, onDelete, formatTime,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  useEffect(() => {
    // Lock body scroll + remember previous focus for restoration on close
    previousActiveElement.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); onPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); onNext(); }
    };
    window.addEventListener('keydown', handleKey);

    // Move focus into dialog
    queueMicrotask(() => dialogRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
      const prev = previousActiveElement.current as HTMLElement | null;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [onClose, onPrev, onNext]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isVideo = story.mediaType === 'video';
  const mediaUrl = story.mediaUrl || '';
  const canPrev = index > 0;
  const canNext = index >= 0 && index < stories.length - 1;
  const displayName = currentUser.displayName || adminUser.displayName;
  const reviewerAvatar = currentUser.avatar || adminUser.avatar;

  const content = (
    <div
      className="story-theater-backdrop"
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="story-theater-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Story viewer"
        tabIndex={-1}
        onMouseDown={e => e.stopPropagation()}
      >
        <button className="st-close" onClick={onClose} aria-label="Close (Esc)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <div className="st-stage">
          {/* Blurred matte layer — same media, scaled and blurred for premium look */}
          {!isVideo && mediaUrl && (
            <div
              className="st-matte"
              style={{ backgroundImage: `url(${mediaUrl})` }}
              aria-hidden="true"
            />
          )}
          <div className="st-matte-overlay" aria-hidden="true" />

          {/* Real media — contain, never crop */}
          <div className="st-media-wrap">
            {isVideo ? (
              <video src={mediaUrl} controls autoPlay playsInline className="st-media" />
            ) : (
              <img
                src={mediaUrl}
                alt=""
                className="st-media"
                onError={e => { (e.target as HTMLImageElement).style.opacity = '0.4'; }}
              />
            )}
          </div>

          {/* Prev / Next on the media itself */}
          {canPrev && (
            <button className="st-nav prev" onClick={onPrev} aria-label="Previous story (←)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
          )}
          {canNext && (
            <button className="st-nav next" onClick={onNext} aria-label="Next story (→)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          )}

          {/* Index pill */}
          {stories.length > 1 && (
            <span className="st-counter">{index + 1} / {stories.length}</span>
          )}
        </div>

        <aside className="st-panel">
          <div className="st-panel-header">
            <img src={reviewerAvatar} alt="" className="st-avatar" onError={e => { (e.target as HTMLImageElement).style.opacity = '0.4'; }}/>
            <div className="st-meta">
              <span className="st-name">{displayName}</span>
              <span className="st-time">{formatTime(story.timestamp)}</span>
            </div>
          </div>

          <dl className="st-facts">
            <div><dt>Media type</dt><dd>{isVideo ? 'Video' : 'Image'}</dd></div>
            {story.expiresAt && (
              <div><dt>Expires</dt><dd>{formatTime(story.expiresAt)}</dd></div>
            )}
            <div><dt>Status</dt><dd className="st-status-pill">Active</dd></div>
          </dl>

          {story.content && (
            <div className="st-caption">
              <h4>Caption</h4>
              <p>{story.content}</p>
            </div>
          )}

          <div className="st-actions">
            <button
              className="st-action st-action-copy"
              onClick={() => { if (mediaUrl) navigator.clipboard.writeText(mediaUrl); }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
              </svg>
              Copy media URL
            </button>
            <button className="st-action st-action-delete" onClick={onDelete}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
              </svg>
              Delete story
            </button>
          </div>

          <p className="st-keys">
            <kbd>Esc</kbd> close · <kbd>←</kbd> <kbd>→</kbd> navigate
          </p>
        </aside>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default StoryTheater;
